import React from 'react';
import { Fab, styled, keyframes, Zoom } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import { useChat } from '../models/ChatContext';
import { init, startConversation } from '../controllers/ChatController';

// Animation pour l'icône active
const pulse = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(102, 126, 234, 0.7);
  }
  70% {
    transform: scale(1.05);
    box-shadow: 0 0 0 10px rgba(102, 126, 234, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(102, 126, 234, 0);
  }
`;

const wave = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
`;

const FloatingButton = styled(Fab)(({ theme, isActive }) => ({
  position: 'fixed',
  bottom: '100px', // Déplacé plus haut pour éviter la bottom bar (70px + 30px de marge)
  right: '24px',
  zIndex: 1300,
  width: '64px',
  height: '64px',
  background: isActive 
    ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  boxShadow: `
    0 8px 32px ${isActive ? 'rgba(16, 185, 129, 0.4)' : 'rgba(102, 126, 234, 0.4)'},
    inset 0 1px 0 rgba(255, 255, 255, 0.2)
  `,
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  animation: isActive ? `${pulse} 2s infinite, ${wave} 1s infinite` : 'none',
  '&:hover': {
    transform: 'translateY(-2px) scale(1.05)',
    background: isActive 
      ? 'linear-gradient(135deg, #059669 0%, #047857 100%)'
      : 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
    boxShadow: `
      0 12px 40px ${isActive ? 'rgba(16, 185, 129, 0.5)' : 'rgba(102, 126, 234, 0.5)'},
      inset 0 1px 0 rgba(255, 255, 255, 0.2)
    `,
  },
  '&:active': {
    transform: 'translateY(0) scale(0.95)',
  },
  '& .MuiSvgIcon-root': {
    fontSize: '28px',
    filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))',
  },
}));

const FloatingChatButton = () => {
  const { 
    chatActive, 
    setChatActive, 
    setLoading, 
    peerConnectionRef, 
    dataChannelRef, 
    setResponseTexts 
  } = useChat();

  const handleChatToggle = () => {
    if (chatActive) {
      // Arrêter la conversation
      if (peerConnectionRef.current) {
        peerConnectionRef.current.close();
        peerConnectionRef.current = null;
      }
      setChatActive(false);
    } else {
      // Démarrer la conversation
      init(setLoading, peerConnectionRef, dataChannelRef, setResponseTexts);
      setChatActive(true);
    }
  };

  return (
    <Zoom in={true} timeout={500}>
      <FloatingButton
        isActive={chatActive}
        onClick={handleChatToggle}
        aria-label={chatActive ? "Arrêter la conversation" : "Démarrer la conversation"}
      >
        {chatActive ? <MicIcon /> : <MicOffIcon />}
      </FloatingButton>
    </Zoom>
  );
};

export default FloatingChatButton; 